import React, { useEffect, useState, useRef } from "react";
import { ImageViewer } from "@/components/ImageViewer";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, ImagePlus, Smile, Check, CheckCheck, Heart, Forward } from "lucide-react";
import ForwardDialog from "@/components/ForwardDialog";
import api from "@/lib/api";
import { timeAgo } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";

import { GS_EMOJIS, EmojiText, isOnlyEmoji } from "@/lib/emoji";

export default function MessagesPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user, setAuthOpen, refreshBadges } = useAuth();
  const { confirm } = useConfirm();
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [viewImg, setViewImg] = useState(null);
  const [fwdImg, setFwdImg] = useState(null);
  const chatRef = useRef(null);
  const [chatH, setChatH] = useState(null);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const lastTypeRef = useRef(0);
  const pressRef = useRef(null);
  const suppressRef = useRef(false);
  const lastCountRef = useRef(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const startPress = (fn) => { suppressRef.current = false; pressRef.current = setTimeout(() => { suppressRef.current = true; fn(); }, 450); };
  const cancelPress = () => { if (pressRef.current) { clearTimeout(pressRef.current); pressRef.current = null; } };

  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  useEffect(() => {
    if (!thread) return;
    const fit = () => {
      if (!chatRef.current) return;
      const top = chatRef.current.getBoundingClientRect().top;
      const nav = document.querySelector("nav.fixed.bottom-0");
      const navH = nav ? nav.getBoundingClientRect().height : 70;
      setChatH(Math.max(300, window.innerHeight - top - navH - 16));
    };
    const t = setTimeout(fit, 0);
    window.addEventListener("resize", fit);
    return () => { clearTimeout(t); window.removeEventListener("resize", fit); };
  }, [thread]);

  useEffect(() => {
    if (!userId || !navigator.serviceWorker) return;
    navigator.serviceWorker.ready.then((r) => r.getNotifications({ tag: `msg-${userId}` })).then((ns) => ns.forEach((n) => n.close())).catch(() => {});
  }, [userId]);


  useEffect(() => {
    if (!user || user === false) return;
    if (userId) {
      api.get(`/messages/${userId}`).then((r) => { setThread(r.data); refreshBadges && refreshBadges(); }).catch(() => {});
    } else {
      api.get("/messages").then((r) => setConvos(r.data)).catch(() => {});
    }
  }, [userId, user]);

  useEffect(() => {
    const n = thread?.messages?.length || 0;
    if (n !== lastCountRef.current) { lastCountRef.current = n; endRef.current?.scrollIntoView(); }
    else if (otherTyping) endRef.current?.scrollIntoView();
  }, [thread, otherTyping]);

  useEffect(() => {
    if (!userId || !(user && user !== false)) return;
    const id = setInterval(() => {
      api.get(`/messages/${userId}/typing`).then((r) => setOtherTyping(r.data.typing)).catch(() => {});
      api.get(`/messages/${userId}`).then((r) => setThread((prev) => prev ? { ...prev, messages: r.data.messages, user: r.data.user } : r.data)).catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [userId, user]);

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (now - lastTypeRef.current > 2000) {
      lastTypeRef.current = now;
      api.post(`/messages/${userId}/typing`).catch(() => {});
    }
  };

  const delMessage = async (m) => {
    if (!(await confirm({ title: "Fshi mesazhin", message: "Ta fshijmë këtë mesazh? Fshihet vetëm për ty." }))) return;
    try { await api.delete(`/messages/msg/${m.id}`); setThread((t) => ({ ...t, messages: t.messages.filter((x) => x.id !== m.id) })); toast.success("U fshi"); } catch { /* ignore */ }
  };
  const likeMsg = async (m) => {
    try { const r = await api.post(`/messages/msg/${m.id}/like`); setThread((t) => ({ ...t, messages: t.messages.map((x) => (x.id === m.id ? { ...x, likes: r.data.likes } : x)) })); } catch { /* ignore */ }
  };
  const delConvo = async (c) => {
    if (!(await confirm({ title: "Fshi bisedën", message: `Ta fshijmë bisedën me ${c.name}? Fshihet vetëm për ty, ${c.name} e ruan bisedën.` }))) return;
    try { await api.delete(`/messages/convo/${c.user_id}`); setConvos((cs) => cs.filter((x) => x.user_id !== c.user_id)); toast.success("Biseda u fshi"); } catch { /* ignore */ }
  };

  if (!user || user === false) return <div className="p-8 text-center text-slate-500">Kyçu për të parë mesazhet.</div>;

  const send = async (emoji) => {
    const body = typeof emoji === "string" ? emoji : text;
    if (!body.trim()) return;
    const r = await api.post("/messages", { to_user: userId, body });
    setThread((t) => ({ ...t, messages: [...t.messages, r.data] }));
    setText(""); setShowEmoji(false);
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSending(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.post("/messages", { to_user: userId, body: "", image: up.data.url });
      setThread((t) => ({ ...t, messages: [...t.messages, r.data] }));
    } catch { /* ignore */ }
    finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (userId && thread) {
    return (
      <div ref={chatRef} className="flex flex-col h-[calc(100vh-180px)] w-full max-w-full overflow-x-hidden" style={chatH ? { height: chatH } : undefined}>
        {viewImg && <ImageViewer src={viewImg} onClose={() => setViewImg(null)} />}
        {fwdImg && <ForwardDialog image={fwdImg} onClose={() => setFwdImg(null)} />}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white sticky top-[57px] z-10">
          <button data-testid="messages-back" onClick={() => nav("/mesazhe")}><ArrowLeft className="w-5 h-5" /></button>
          <button onClick={() => nav(`/perdorues/${thread.user?.id}`)} className="relative w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold overflow-hidden">{thread.user?.avatar ? <img src={thread.user.avatar} alt="" className="w-full h-full object-cover" /> : thread.user?.name?.[0]}<span className={`absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full ${thread.user?.online ? "bg-emerald-500" : "bg-slate-300"}`} /></button>
          <div className="font-semibold text-slate-800 min-w-0 truncate" onClick={() => nav(`/perdorues/${thread.user?.id}`)}>{thread.user?.name}{thread.user?.online && <span className="ml-2 text-[11px] font-normal text-emerald-500">online</span>}</div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 flex flex-col" data-testid="chat-scroll">
          <div className="mt-auto space-y-2">
          {thread.messages.map((m) => {
            const mine = m.from_user === user.id;
            const press = {
              onTouchStart: () => startPress(() => delMessage(m)), onTouchEnd: cancelPress, onTouchMove: cancelPress,
              onMouseDown: () => startPress(() => delMessage(m)), onMouseUp: cancelPress, onMouseLeave: cancelPress,
            };
            const liked = (m.likes || []).includes(user.id);
            if (m.image) {
              const stop = { onMouseDown: (e) => e.stopPropagation(), onTouchStart: (e) => e.stopPropagation() };
              return (
                <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-photo-msg-${m.id}`}>
                  <div className={`flex flex-col gap-2 pb-6 ${mine ? "order-1" : "order-2"}`} {...stop}>
                    <button data-testid={`chat-like-${m.id}`} onClick={() => likeMsg(m)} aria-label="Pëlqe" className="w-9 h-9 rounded-full bg-white border border-slate-200 shadow-sm flex flex-col items-center justify-center text-slate-500">
                      <Heart className={`w-4 h-4 ${liked ? "fill-rose-500 text-rose-500" : ""}`} />
                      {(m.likes || []).length > 0 && <span className="text-[9px] font-bold leading-none -mt-0.5">{m.likes.length}</span>}
                    </button>
                    <button data-testid={`chat-forward-${m.id}`} onClick={() => setFwdImg(m.image)} aria-label="Ndaje" className="w-9 h-9 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500">
                      <Forward className="w-4 h-4" />
                    </button>
                  </div>
                  <div {...press} className={`max-w-[70%] min-w-0 select-none cursor-pointer flex flex-col ${mine ? "order-2 items-end" : "order-1 items-start"}`}>
                    <img src={m.image} alt="" data-testid={`chat-img-${m.id}`} onClick={(e) => { e.stopPropagation(); cancelPress(); setViewImg(m.image); }} className="rounded-2xl max-h-64 max-w-full w-auto shadow-sm border border-slate-100" draggable={false} />
                    {m.body && <div className={`mt-1 rounded-2xl px-3.5 py-2 text-sm break-words [overflow-wrap:anywhere] whitespace-pre-wrap ${mine ? "bg-emerald-500 text-white" : "bg-white border border-slate-100 text-slate-800"}`}><EmojiText body={m.body} big={false} /></div>}
                    <div className="text-[10px] mt-1 flex items-center gap-1 text-slate-400">
                      <span>{timeAgo(m.created_at)}</span>
                      {mine && (m.read
                        ? <span data-testid={`seen-${m.id}`} className="flex items-center gap-0.5"><CheckCheck className="w-3.5 h-3.5" />Parë</span>
                        : <Check data-testid={`sent-${m.id}`} className="w-3.5 h-3.5" />)}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div {...press}
                  className={`max-w-[75%] min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm select-none cursor-pointer ${isOnlyEmoji(m.body) ? (mine ? "!bg-transparent !border-0 !px-0 text-slate-400 " : "!bg-transparent !border-0 !px-0 ") : ""}${mine ? "bg-emerald-500 text-white rounded-br-sm" : "bg-white border border-slate-100 text-slate-800 rounded-bl-sm"}`}>
                  <EmojiText body={m.body} big={isOnlyEmoji(m.body)} />
                  <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${mine ? (isOnlyEmoji(m.body) ? "text-slate-400 justify-end" : "text-white/80 justify-end") : "text-slate-400"}`}>
                    <span>{timeAgo(m.created_at)}</span>
                    {mine && (m.read
                      ? <span data-testid={`seen-${m.id}`} className="flex items-center gap-0.5"><CheckCheck className="w-3.5 h-3.5" />Parë</span>
                      : <Check data-testid={`sent-${m.id}`} className="w-3.5 h-3.5" />)}
                  </div>
                </div>
              </div>
            );
          })}
          {!thread.messages.length && <p className="text-center text-slate-400 text-sm py-6">Fillo bisedën</p>}
          {otherTyping && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-400 italic flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                <span className="ml-1">Po shkruan...</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
          </div>
        </div>
        {showEmoji && (
          <div className="grid grid-cols-5 gap-2 px-4 py-3 border-t border-slate-100 bg-white max-h-60 overflow-y-auto" data-testid="emoji-picker">
            {GS_EMOJIS.map((em, i) => <button key={em} data-testid={`emoji-${i + 1}`} onClick={() => send(em)} className="active:scale-90 transition-transform"><img src={`/emoji/e${i + 1}.webp?v=2`} alt="" className="w-full aspect-square" draggable={false} /></button>)}
          </div>
        )}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-100 bg-white">
          <input ref={fileRef} type="file" accept="image/*" onChange={sendImage} className="hidden" data-testid="message-image-input" />
          <button data-testid="message-image-btn" onClick={() => fileRef.current?.click()} disabled={sending} className="text-slate-400 p-1 shrink-0"><ImagePlus className="w-5 h-5" /></button>
          <button data-testid="message-emoji-btn" onClick={() => setShowEmoji((s) => !s)} className="text-slate-400 p-1 shrink-0"><Smile className="w-5 h-5" /></button>
          <input data-testid="message-input" value={text} onChange={onType} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Shkruaj mesazh..." className="flex-1 min-w-0 bg-slate-100 rounded-full px-4 h-11 outline-none text-sm" />
          <button data-testid="send-message" onClick={() => send()} className="bg-emerald-500 text-white rounded-full p-3 shrink-0"><Send className="w-5 h-5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="gs-fade px-4 pt-4">
      <h1 className="text-2xl font-extrabold text-slate-900 mb-3">Mesazhet</h1>
      <div className="space-y-2">
        {convos.map((c) => (
          <button
            key={c.user_id} data-testid={`conversation-${c.user_id}`}
            onClick={() => { if (suppressRef.current) { suppressRef.current = false; return; } nav(`/mesazhe/${c.user_id}`); }}
            onTouchStart={() => startPress(() => delConvo(c))} onTouchEnd={cancelPress} onTouchMove={cancelPress}
            onMouseDown={() => startPress(() => delConvo(c))} onMouseUp={cancelPress} onMouseLeave={cancelPress}
            className="w-full flex items-center gap-3 bg-white rounded-2xl p-3 border border-slate-100 text-left select-none">
            <div className="relative w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold overflow-hidden shrink-0">{c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" draggable={false} /> : c.name?.[0]}<span className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white rounded-full ${c.online ? "bg-emerald-500" : "bg-slate-300"}`} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800">{c.name}</div>
              <div className="text-sm text-slate-400 truncate"><EmojiText body={c.last} /></div>
            </div>
            {c.unread > 0 && <span className="bg-emerald-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{c.unread}</span>}
          </button>
        ))}
        {!convos.length && <p className="text-center text-slate-400 py-10">Ende pa mesazhe.</p>}
      </div>
    </div>
  );
}
